import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { productsApi } from '@/services/api';

interface FilterOptionsState {
  brands: string[];
  conditions: string[];
  productTypes: string[];
  locations: string[];
  priceRange: {
    min: number;
    max: number;
  };
  isLoading: boolean;
  error: string | null;
}

const initialState: FilterOptionsState = {
  brands: [],
  conditions: [],
  productTypes: [],
  locations: [],
  priceRange: {
    min: 0,
    max: 10000
  },
  isLoading: false,
  error: null,
};

// Async thunks for fetching filter options
export const fetchBrands = createAsyncThunk(
  'filterOptions/fetchBrands',
  async (_, { rejectWithValue }) => {
    try {
      const response = await productsApi.getBrands();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch brands');
    }
  }
);

export const fetchConditions = createAsyncThunk(
  'filterOptions/fetchConditions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await productsApi.getConditions();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch conditions');
    }
  }
);

export const fetchProductTypes = createAsyncThunk(
  'filterOptions/fetchProductTypes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await productsApi.getProductTypes();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch product types');
    }
  }
);

export const fetchLocations = createAsyncThunk(
  'filterOptions/fetchLocations',
  async (_, { rejectWithValue }) => {
    try {
      const response = await productsApi.getLocations();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch locations');
    }
  }
);

export const fetchPriceRange = createAsyncThunk(
  'filterOptions/fetchPriceRange',
  async (_, { rejectWithValue }) => {
    try {
      const response = await productsApi.getPriceRange();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch price range');
    }
  }
);

export const fetchAllFilterOptions = createAsyncThunk(
  'filterOptions/fetchAll',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      await Promise.all([
        dispatch(fetchBrands()),
        dispatch(fetchConditions()),
        dispatch(fetchProductTypes()),
        dispatch(fetchLocations()),
        dispatch(fetchPriceRange()),
      ]);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch filter options');
    }
  }
);

const filterOptionsSlice = createSlice({
  name: 'filterOptions',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch brands
    builder
      .addCase(fetchBrands.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBrands.fulfilled, (state, action: PayloadAction<string[]>) => {
        state.isLoading = false;
        state.brands = action.payload;
      })
      .addCase(fetchBrands.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch conditions
    builder
      .addCase(fetchConditions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchConditions.fulfilled, (state, action: PayloadAction<string[]>) => {
        state.isLoading = false;
        state.conditions = action.payload;
      })
      .addCase(fetchConditions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch product types
    builder
      .addCase(fetchProductTypes.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProductTypes.fulfilled, (state, action: PayloadAction<string[]>) => {
        state.isLoading = false;
        state.productTypes = action.payload;
      })
      .addCase(fetchProductTypes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch locations
    builder
      .addCase(fetchLocations.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchLocations.fulfilled, (state, action: PayloadAction<string[]>) => {
        state.isLoading = false;
        state.locations = action.payload;
      })
      .addCase(fetchLocations.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch price range
    builder
      .addCase(fetchPriceRange.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPriceRange.fulfilled, (state, action: PayloadAction<{ min: number; max: number }>) => {
        state.isLoading = false;
        state.priceRange = action.payload;
      })
      .addCase(fetchPriceRange.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch all filter options
    builder
      .addCase(fetchAllFilterOptions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllFilterOptions.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(fetchAllFilterOptions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError } = filterOptionsSlice.actions;
export default filterOptionsSlice.reducer;

